import {addDays, differenceInDays} from "date-fns";
import {useEffect} from "react";
import {useCookies} from "react-cookie";

import {Alert} from "@/components/alert/Alert.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {GLOBALPING_NETWORK_CHECK} from "@/lib/featureFlags.ts";
import {AccountSubscriptionType} from "@/lib/types/accounts.ts";

const COOKIE = "globalping_network_banner";
const MAX_DISPLAY_DAYS = 10;
const DOCUMENTATION_URL = "https://support.uptime.com/hc/en-us/articles/29039899075484-Network-Check-Basics";

type GlobalPingBannerCookie = {
  firstSeenAt: string;
  dismissed?: boolean;
};

const cookieOptions = () => ({path: "/", expires: addDays(new Date(), 30)});

export const GlobalPingBanner = () => {
  const {user, userHasFeature} = useCurrentUserContext();
  const [cookies, setCookie] = useCookies([COOKIE]);

  const isBilling2 = user?.account.subscription_type === AccountSubscriptionType.BILLING_2_0;
  const eligible = isBilling2 && !!userHasFeature(GLOBALPING_NETWORK_CHECK);
  const state = cookies[COOKIE] as GlobalPingBannerCookie | undefined;

  useEffect(() => {
    if (eligible && !state) {
      setCookie(COOKIE, {firstSeenAt: new Date().toISOString()}, cookieOptions());
    }
  }, [eligible, state, setCookie]);

  if (!eligible || state?.dismissed) {
    return null;
  }
  if (state?.firstSeenAt && differenceInDays(new Date(), new Date(state.firstSeenAt)) >= MAX_DISPLAY_DAYS) {
    return null;
  }

  const handleDismiss = () => {
    setCookie(COOKIE, {firstSeenAt: state?.firstSeenAt ?? new Date().toISOString(), dismissed: true}, cookieOptions());
  };

  return (
    <Alert variant="verification" dismissible onClear={handleDismiss}>
      We have released a new Check type: Network. Network Checks run from ISP and final mile networks, greatly expanding
      the geography and carriers from which you can execute Checks. Pick your locations and carriers, and capture the
      full network path with an optional Traceroute. See{" "}
      <a href={DOCUMENTATION_URL} target="_blank" rel="noopener noreferrer">
        documentation
      </a>{" "}
      for additional information.
    </Alert>
  );
};
